import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { submitKeysOf } from '../components/workflow/useWorkflowPerms';
import NowName from '../components/shared/NowName';
import WorkflowListShell from '../components/workflow/WorkflowListShell';
import { JoinedNamesCell } from '../components/workflow/CoverageDisplay';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import ReviewerLapsedNote from '../components/workflow/ReviewerLapsedNote';
import { formatDateShort } from '../utils/dateFormat';

// Signed Financial Statements submissions — list page (docs/modules/workflow-fs.md). QC35 is
// its own flow; the Type column keeps legacy qc35/both
// rows readable.
export const FS_TYPE_LABELS = { fs: 'Signed Financial Statements', qc35: 'QC35 Form', both: 'Signed Financial Statements + QC35 Form' };

const WorkflowFs = () => {
  const { hasPermission } = useAuth();

  return (
    <WorkflowListShell
      title="Signed Financial Statements"
      subtitle="Signed financial statements submissions."
      newTo="/workflow/fs/new"
      canCreate={submitKeysOf('fs').some(hasPermission)}
      apiPath="/api/workflow/fs"
      searchPlaceholder="Search submission / engagement / requester…"
      rowTo={(r) => `/workflow/fs/${r.id}`}
      columns={[
        {
          key: 'requestNo',
          label: 'Submission',
          sortKey: 'requestNo',
          className: 'whitespace-nowrap',
          render: (r) => (
            <Link
              to={`/workflow/fs/${r.id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              {r.requestNo}
            </Link>
          ),
        },
        { key: 'requestType', label: 'Type', sortKey: 'requestType', className: 'text-slate-700 whitespace-nowrap', render: (r) => FS_TYPE_LABELS[r.requestType] },
        { key: 'engagement', label: 'Engagement', sortKey: 'engagement', className: 'text-slate-900', render: (r) => <JoinedNamesCell value={r.engagementNames} fallback={r.engagementName} now={r.engagementNamesNow || r.engagementNameNow} serviceDescription={r.firstServiceDescription} /> },
        { key: 'signingDate', label: 'Signing Date', sortKey: 'signingDate', className: 'text-slate-700 whitespace-nowrap', render: (r) => (r.signingDate ? formatDateShort(r.signingDate) : '—') },
        { key: 'status', label: 'Status', sortKey: 'status', render: (r) => (<><WorkflowStatusBadge status={r.status} /><ReviewerLapsedNote when={r.reviewerLapsed} /></>) },
        // 這條流程沒有審核關，所以這一欄講的是「怎麼核准的」：AI 審核通過、
        // 送出即核准，或改制前由點名的審核人核准（那時才有人名）。
        {
          key: 'reviewer',
          label: 'Approval',
          sortKey: 'reviewer',
          className: 'whitespace-nowrap',
          render: (r) => {
            if (r.aiApproved) {
              return (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700">
                  AI review
                </span>
              );
            }
            if (r.autoApproved) {
              return (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
                  On submission
                </span>
              );
            }
            return <NowName value={r.reviewerName} now={r.reviewerNameNow} className="text-slate-600" />;
          },
        },
        { key: 'requester', label: 'Requester', sortKey: 'requester', className: 'text-slate-600', render: (r) => r.requesterEmail },
        { key: 'createdAt', label: 'Submitted', sortKey: 'createdAt', className: 'text-slate-600 whitespace-nowrap', render: (r) => formatDateShort(r.createdAt) },
      ]}
    />
  );
};

export default WorkflowFs;
